import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { FormControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { BuilderRequest } from 'src/app/models/requests';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { debounceTime, Subject, Subscription } from 'rxjs';
import { MinimumWordCountValidator } from 'src/app/directives/minimum-word-count-validator.directive';
import { ClearanceLevels, ClearancelevelEnum } from 'src/app/constants/clearance-levels.enum';
import { WorkgroupInformationModel } from 'src/app/models/workgroup-information.model';
import { TranslateService } from '@ngx-translate/core';
import { PKIUrls } from 'src/app/constants/pkiurls.constants';
import { RequestBuilderService } from '../request-builder.service';
import { DossierService } from 'src/app/services/dossier.service';
import { ToastService } from 'src/app/services/toast.service';

@Component({
  selector: 'app-request-title',
  templateUrl: './request-title.component.html'
})
export class RequestTitleComponent implements OnInit, OnDestroy {
  public ClearancelevelUrl: string = PKIUrls.ClearancelevelUrl;
  public signYourself: boolean = false;
  public currentTab: string = "AddDocuments";
  workflowSteps: string[] = [];

  get request(): BuilderRequest {
    return this._request;
  }
  @Input() set request(val: BuilderRequest) {
    this.allowedWorkgroups.forEach(item => {
      if (item.Guid === val.workgroup) {
        this.maxClearanceLevelAllowed = (item.Clearancelevel as ClearancelevelEnum);
        if (this.maxClearanceLevelAllowed !== ClearancelevelEnum.One) {
          this.showClearancelevelPicker = true;
        } else {
          this.showClearancelevelPicker = false;
        }
      }
    });
    this._request = val
    this.requestClearanceControl.setValue(this.request.clearancelevel);
    this.selectedWorkgroupName = this.request.workgroup ? this.getWorkgroupName(this.request.workgroup) : "";
    this.requestWorkgroupControl.setValue(this.request.workgroup);
  }
  private _request!: BuilderRequest;
  requestTitleControl: FormControl;
  requestWorkgroupControl: FormControl;
  requestClearanceControl: FormControl;
  form: UntypedFormGroup;

  showClearancelevelPicker: boolean = false;
  isInFirstSendgroup: boolean = false;
  selectedWorkgroupName: string = "";
  allowedWorkgroups = this.authenticationService.workgroups;
  allowedClearanceLevels = ClearanceLevels;


  worflowTypeSubscription: Subscription | null = null;
  currentTabSubscription: Subscription | null = null;
  workflowStepsSubscription: Subscription | null = null;
  isInFirstSendgroupSubscription: Subscription | null = null;
  requestSubscription: Subscription | null = null;

  /**
   * We only display clearance levels BELOW or MATCHING the given level.
   */
  maxClearanceLevelAllowed: ClearancelevelEnum = ClearancelevelEnum.Five;
  /**
   * A delayed update pipe to prevent spamming of updates (live save)
   */
  requestChangeDelaySubject = new Subject()
  /**
   * Whether the 'Go to request' button is DISABLED
   * Visible is treated differently
   */
  get isGotoRequestDisabled(): boolean {
    return !this.completeValidationSummary || !this.completeValidationSummary.passedValidations;
  }
  /**
   * BIG COMPLETE VALIDATION SUMMARY
   */
  public completeValidationSummary: any;
  validationSummarySubscription: Subscription | null = null;


  constructor(private formBuilder: UntypedFormBuilder, private toastService: ToastService, private dossierService: DossierService, private authenticationService: AuthenticationService, private translateService: TranslateService, private requestBuilderService: RequestBuilderService) {
    this.requestTitleControl = new FormControl('',
      [
        Validators.required,
        Validators.minLength(4),
        Validators.maxLength(500),
        MinimumWordCountValidator(1)
      ]
    );
    this.requestWorkgroupControl = new FormControl('', []);
    this.requestClearanceControl = new FormControl<ClearancelevelEnum>(ClearancelevelEnum.One, []);
    this.form = this.formBuilder.group(
      {
        requestTitleControl: this.requestTitleControl,
        requestWorkgroupControl: this.requestWorkgroupControl,
        requestClearanceControl: this.requestClearanceControl
      }
    );

    // if workgroups not loaded/set, (re)Load?
    if (this.allowedWorkgroups.length == 0) {
      this.authenticationService.ResetUserInfo();
      this.allowedWorkgroups = this.authenticationService.workgroups;
    }
    this.currentTabSubscription = this.requestBuilderService.currentTab$.subscribe(currentTab => {
      this.currentTab = currentTab;
    })
    this.workflowStepsSubscription = this.requestBuilderService.workflowSteps$.subscribe(steps => {
      this.workflowSteps = steps;
    });
    this.isInFirstSendgroupSubscription = this.requestBuilderService.isInFirstSendgroup$.subscribe(isInFirstSendgroup => {
      this.isInFirstSendgroup = isInFirstSendgroup;
    });
    this.validationSummarySubscription = this.requestBuilderService.validationSummary$.subscribe((summary) => {
      this.completeValidationSummary = summary;
    });
    this.requestSubscription = this.requestBuilderService.request$.subscribe(request => {
      this.request = request;
      if (this.request) {
        if (!this.requestTitleControl.value) {
          this.requestTitleControl.setValue(this.request.jobName);
        }
        this.requestWorkgroupControl.setValue(this.request.workgroup);
        this.requestClearanceControl.setValue(this.request.clearancelevel);
      }
    });
  }

  async updateDossier() {
    if (this.request && this.request.id) {
      (await this.dossierService.updateDossier(this.request.id, this.request.jobName, this.request.workgroup, this.request.clearancelevel)).subscribe({
        next: (data: any) => {
          this.toastService.showSaved();
        },
        error: () => this.toastService.showErrorByKey("Toasts.UpdateDossierError"),
      });
    }
  }

  ngOnInit(): void {
    let seenInformationItem = localStorage.getItem("seenMoreInformationAlertRequestBuilder");
    if (!seenInformationItem) {
      let element = document.getElementById("moreInformationAlert") as HTMLElement;
      if (element) {
        element.classList.remove("d-none");
        element.classList.add("d-flex");
      }
    }
    // setup the delayed update pipe to prevent spamming of updates (live save)
    this.requestChangeDelaySubject.pipe(
      debounceTime(1000)
    ).subscribe({
      next: () => { this.updateDossier() }
    });
  }

  public TriggerUpdateRequest(updatedRequest: BuilderRequest) {
    this.updateDossier();
  }
  ngOnDestroy() {
    if (this.worflowTypeSubscription) {
      this.worflowTypeSubscription.unsubscribe();
    }
    if (this.currentTabSubscription) {
      this.currentTabSubscription.unsubscribe();
    }
    if (this.workflowStepsSubscription) {
      this.workflowStepsSubscription.unsubscribe();
    }
    if (this.isInFirstSendgroupSubscription) {
      this.isInFirstSendgroupSubscription?.unsubscribe();
    }
    if (this.validationSummarySubscription) {
      this.validationSummarySubscription?.unsubscribe();
    }
    if (this.requestSubscription) {
      this.requestSubscription?.unsubscribe();
    }

  }

  UpdateTitle() {
    let inputValue = this.requestTitleControl.value;
    if (inputValue && this.requestTitleControl.valid) {
      this.request.jobName = inputValue;
      this.requestChangeDelaySubject.next({});
    }
  }

  changeCurrentClearancelevel(selectedClearancelevel: ClearancelevelEnum) {
    this.request.clearancelevel = selectedClearancelevel;
    this.requestChangeDelaySubject.next({});
  }

  changeWorkgroup(selectedWorkGroup: WorkgroupInformationModel | null) {
    // picking the no workgroup option
    if (selectedWorkGroup == null) {
      this.request.workgroup = "";
      this.selectedWorkgroupName = this.translateService.instant("Request.RequestTitle.Noworkgroup");
      this.maxClearanceLevelAllowed = ClearancelevelEnum.One
      this.request.clearancelevel = ClearancelevelEnum.One;
      this.showClearancelevelPicker = false;
      this.requestChangeDelaySubject.next({});
      return;
    }

    // else we have a workgroup -> make it NOT nullable
    let actualWorkgroup = selectedWorkGroup!;
    if (this.request.workgroup == actualWorkgroup.Guid) {
      // no change
      return;
    }
    this.request.workgroup = actualWorkgroup.Guid!;
    this.selectedWorkgroupName = actualWorkgroup.Name!;
    this.maxClearanceLevelAllowed = selectedWorkGroup.Clearancelevel! as ClearancelevelEnum
    this.request.clearancelevel = ClearancelevelEnum.One; // reset regardless of option
    if (this.maxClearanceLevelAllowed !== ClearancelevelEnum.One) {
      this.showClearancelevelPicker = true;
    } else {
      this.showClearancelevelPicker = false;
    }
    this.requestChangeDelaySubject.next({});
  }



  private getWorkgroupName(workgroupId: string): string {
    let workgroups = this.authenticationService.workgroups;
    let workgroup = workgroups.find(s => s.Guid === workgroupId)
    return workgroup?.Name ?? "";
  }

  public isLatestTab() {
    if (!this.workflowSteps || this.workflowSteps.length === 0) {
      return false;
    }
    let tabIndex = this.workflowSteps.findIndex(s => s === this.currentTab);
    return tabIndex === this.workflowSteps.length - 1;
  }

  public async sendRequest() {
    this.requestBuilderService.SendRequest()
  }

  public openMoreInformationPage() {
    let url = PKIUrls.RequestBuilderMoreInformationUrl;
    if (!url) {
      return;
    }
    window.open(url, '_blank');
  }

  public closeMoreInformationPageRequestBuilder() {
    let element = document.getElementById("moreInformationAlert") as HTMLElement;
    if (!element) {
      return;
    } else {
      element.classList.remove("d-flex");
      element.classList.add("d-none");
      const dateObj = new Date();
      const month = dateObj.getUTCMonth() + 1; // months from 1-12
      const day = dateObj.getUTCDate();
      const year = dateObj.getUTCFullYear();
      localStorage.setItem("seenMoreInformationAlertRequestBuilder", `${year}/${month}/${day}`);
    }
  }

}